/* eslint-disable @typescript-eslint/no-explicit-any */
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouteComponentProps, withRouter } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';

import { withAPIService } from '../../networking';
import { withStore } from '../../store';
import { BUTTON_TITLE_TRANSLATION_KEYS } from '../../utils/constants';
import { getFormTemplateFromCustomer } from '../../utils/routeFormatter';

import './CardsAccordion.css';

const EMPTY_ARRAY: unknown[] = [];

type CardsAccordionProps = RouteComponentProps & {
  store: any; // intentionally kept as `any` to preserve behavior
  isOpen: boolean;
  cardTitle: string;
  accordionCallback: (value: number | string) => void;
  cardIndex: number;
  children?: ReactNode;
};

function Component(props: CardsAccordionProps) {
  const {
    store,
    location: { pathname },
    isOpen,
    cardTitle,
    accordionCallback,
    cardIndex,
    children
  } = props;

  const { customer, common } = store;
  const { lineOfCredit } = customer;
  const { policyAccepted } = common;

  const currentPage = pathname?.split('/').reverse()[0];
  const { t } = useTranslation();

  const template = getFormTemplateFromCustomer(customer);
  const formPages = template.pages || EMPTY_ARRAY;

  const [openedAccordion, setOpenedAccordion] = useState<boolean>(false);

  useEffect(() => {
    setOpenedAccordion(isOpen);
  }, [isOpen]);

  const finalCardList = useMemo(() => {
    const pageConfig = formPages.find(
      (page: any) => page.route === currentPage
    );

    let keys = (pageConfig?.cards || []).map((card: any) => card.key);

    if (currentPage === 'financial' && !lineOfCredit) {
      keys = keys.filter((key: string) => key !== 'financialAttachmentCard');
    }

    if (currentPage !== 'review' && policyAccepted) {
      keys = keys.filter((key: string) => key !== 'privacyPolicy');
    }

    return keys;
  }, [formPages, currentPage, lineOfCredit, policyAccepted]);

  const handleAccordionChange = useCallback(() => {
    setOpenedAccordion((prev) => {
      const next = !prev;
      if (!prev) {
        accordionCallback('');
      }
      return next;
    });
  }, [accordionCallback]);

  const handleNextButtonClick = useCallback(() => {
    accordionCallback(cardIndex + 1);
    setOpenedAccordion(false);
  }, [accordionCallback, cardIndex]);

  const handleCloseButtonClick = useCallback(() => {
    setOpenedAccordion(false);
  }, []);

  const baseActionButtonSx = {
    display: 'flex',
    padding: 'var(--padding-padding-base, 8px) var(--padding-padding-lg, 16px)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--spacing-spacing-base, 8px)',
    borderRadius: 'var(--corner-radius-base, 8px)',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '16px',
    minWidth: { xs: '100%', md: '200px' },
    width: { xs: '100%', md: 'auto' },
    maxWidth: '100%',
    height: '40px !important'
  } as const;

  const nextButtonSx = {
    ...baseActionButtonSx,
    background: 'var(--Background-bg-interactive, #00843D)',
    color: '#fff'
  };

  const closeButtonSx = {
    ...baseActionButtonSx,
    borderColor: 'var(--Background-bg-interactive, #00843D)',
    color: 'var(--Background-bg-interactive, #00843D)',
    background: '#fff'
  };

  return (
    <Accordion expanded={openedAccordion} onChange={handleAccordionChange} sx={{ padding: '0' }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1a-content"
        id="panel1a-header"
        className="cardsAccordionSummary"
      >
        <Typography
          sx={{
            color: 'var(--Text-text-subheading, #012912)',
            fontFeatureSettings: '"liga" off, "clig" off',
            fontFamily: 'var(--font-family-helvetica-now, "Helvetica Now For Cargill")',
            fontStyle: 'normal',
            fontWeight: 700,
            fontSize: { xs: '18px', md: '20px' },
            lineHeight: { xs: '24px', md: '26px' },
            letterSpacing: 'var(--font-letter-spacing-wide, 0)'
          }}
        >
          {cardTitle}
        </Typography>
      </AccordionSummary>

      <AccordionDetails className="cardsAccordionDetails">
        {children}

        {finalCardList.length !== cardIndex + 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px',
              flexDirection: 'column'
            }}
          >
            <Button
              type="button"
              onClick={handleNextButtonClick}
              variant="contained"
              sx={nextButtonSx}
            >
              {t(BUTTON_TITLE_TRANSLATION_KEYS.next)}
            </Button>

            <Button
              type="button"
              onClick={handleCloseButtonClick}
              variant="outlined"
              sx={closeButtonSx}
            >
              {t(BUTTON_TITLE_TRANSLATION_KEYS.close)}
            </Button>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// HOC chain preserved exactly
const connectRouter = withRouter(Component);
const connectAPI = withAPIService(connectRouter);
const CardsAccordion = withStore(connectAPI);

export default CardsAccordion;
